/* CLUSTER NAMES FOR PEOPLE. A cluster has an id (icu_cardiogenic_stemi) that is an address, not a name. Wherever the console
   prints a cluster as visible text it prints the title the world publishes for it (a `title` on the cluster in GET
   /v1/world). A world that publishes none is named by its ids set in words (clusterName), and the page that prints them says
   so in one line (clusterNote). The id itself stays in a title attribute and in links, where it is an address. */
import { clusterName } from '../../lib/runs.ts'
import type { PublicWorld } from '../types.ts'

export type ClusterTitles = {
  /** The title to print for a cluster id. */
  of: (id: string) => string
  /** True when the world published a title for every cluster it names. */
  published: boolean
}

type Titled = { id: string; title?: unknown }

/** The titles of a world's clusters. An id the world does not list is set in words, like one it lists without a title. */
export function clusterTitles(clusters: readonly Titled[] | Pick<PublicWorld, 'clusters'> | null | undefined): ClusterTitles {
  const list: readonly Titled[] = !clusters ? [] : Array.isArray(clusters) ? clusters : (clusters as Pick<PublicWorld, 'clusters'>).clusters
  const byId = new Map<string, string>()
  for (const c of list) if (typeof c.title === 'string' && c.title.trim() !== '') byId.set(c.id, c.title.trim())
  return { of: (id) => byId.get(id) ?? clusterName(id), published: list.length > 0 && list.every((c) => byId.has(c.id)) }
}

/** The one line a page prints when the world publishes no titles; null when it does. */
export function clusterNote(t: ClusterTitles): string | null {
  return t.published ? null : 'This world version publishes no cluster titles, so each cluster is named here by its id set in words. The id is in the tooltip.'
}

/** Every snake_case token in a text, which is what a cluster id looks like. Visible text should hold none of them. */
export function snakeIds(text: string): string[] {
  return text.match(/\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+\b/g) ?? []
}
